import React, { useEffect, useState, useRef } from "react";
import {
  MRT_GlobalFilterTextField,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ShowHideColumnsButton,
} from "material-react-table";
import { Box, styled, ThemeProvider, Typography } from "@mui/material";
import moment from "moment";

export const TableWrapper = styled("div")(({
  "& .chipContainer": {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    maxWidth: "100%", // Ensure chips don't overflow the container
    overflow: "hidden",
  },
  "& .MuiChip-root": {
    height: "22px",
  },
  "& .subChipText": {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
  },
  "& .MuiBox-root": {
    boxShadow: "none",
  },
  "& .MuiTableHead-root .MuiIconButton-root, & .MuiToolbar-root .MuiIconButton-root": {
    padding: "0px",
    fontSize: "1.25rem",
    color: "#999999",
  },
  "& .MuiTableRow-root": {
    boxShadow: "none",
  },
  "& .MuiTableRow-root:hover td:after": {
    backgroundColor: "#FFFFFF !important",
  },
  "& .MuiInputBase-input": {
    fontSize: "13px",
    fontWeight: 400,
    padding: '4px 10px !important',
  },
  "& .MuiCollapse-root": {
    width: "100%",
  },
  "& .Mui-TableBodyCell-DetailPanel": {
    padding: "1px",
    backgroundColor: "#ffffff !important",
    filter: "none !important",
    "&:hover": {
      backgroundColor: "#ffffff !important",
      filter: "none !important",
    },
  },
  "& tr.MuiTableRow-root:hover .Mui-TableBodyCell-DetailPanel": {
    backgroundColor: "#ffffff !important",
    filter: "none !important",
  },
  "& .MuiTableRow-root.mrt-table-body-row-detail-panel": {
    backgroundColor: "#ffffff !important",
    filter: "none !important",
    "&:hover": {
      backgroundColor: "#ffffff !important",
      filter: "none !important",
    },
  },
  "& .MuiTableRow-root.Mui-TableBodyRow-DetailPanel": {
    backgroundColor: "#ffffff !important",
    filter: "none !important",
    "&:hover": {
      backgroundColor: "#ffffff !important",
      filter: "none !important",
    },
  },
  "& .MuiTableRow-root.mrt-table-body-row-detail-panel:hover": {
    backgroundColor: "#ffffff !important",
    filter: "none !important",
  },
  "& .MuiTableRow-root.Mui-TableBodyRow-DetailPanel:hover": {
    backgroundColor: "#ffffff !important",
    filter: "none !important",
  },
}));

export const parseToDateString = (val: any): string => {
  if (val === undefined || val === null || val === '') {
    return '';
  }
  if (val && typeof val === 'object' && typeof val.format === 'function' && typeof val.isValid === 'function') {
    return val.isValid() ? val.format('YYYY-MM-DD') : '';
  }
  if (moment.isMoment(val)) {
    return val.isValid() ? val.format('YYYY-MM-DD') : '';
  }
  if (Object.prototype.toString.call(val) === '[object Date]') {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (typeof val === 'string') {
    const cleanVal = val.trim();
    if (!cleanVal) return '';

    // If it contains a time component (e.g. ISO string like 2026-05-01T09:53:04 or time 12:30), parse using moment to preserve local timezone rendering matching the cell view
    const hasTime = /T|\d{1,2}:\d{2}/.test(cleanVal);
    if (!hasTime) {
      // YYYY-MM-DD or YYYY/MM/DD with loose digits
      const matchYMD = cleanVal.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
      if (matchYMD) {
        const y = matchYMD[1];
        const m = matchYMD[2].padStart(2, '0');
        const d = matchYMD[3].padStart(2, '0');
        return `${y}-${m}-${d}`;
      }

      // MM/DD/YYYY or DD/MM/YYYY with loose digits (we assume US format MM/DD/YYYY first)
      const matchMDY = cleanVal.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
      if (matchMDY) {
        const m = matchMDY[1].padStart(2, '0');
        const d = matchMDY[2].padStart(2, '0');
        const y = matchMDY[3];
        return `${y}-${m}-${d}`;
      }
    }

    // Default fallback to robust moment parsing (interprets ISO string correctly with timezone offset)
    const m = moment(cleanVal);
    if (m.isValid()) {
      return m.format('YYYY-MM-DD');
    }
  }
  if (typeof val === 'number') {
    const m = moment(val);
    if (m.isValid()) {
      return m.format('YYYY-MM-DD');
    }
  }
  return '';
};

export const customDateFilterFn = (row: any, id: string, filterValue: any) => {
  console.log("[CUSTOM TABLE DATE FILTER] ID:", id, "Filter Value:", filterValue, "Type:", typeof filterValue);
  if (filterValue === undefined || filterValue === null || filterValue === '') {
    return true;
  }
  const rowValue = row.getValue(id);
  console.log("[CUSTOM TABLE DATE FILTER] Row Value:", rowValue, "Type:", typeof rowValue);
  if (rowValue === undefined || rowValue === null || rowValue === '') {
    return false;
  }

  // Try to parse both filterValue and rowValue
  const filterDateStr = parseToDateString(filterValue);
  const rowDateStr = parseToDateString(rowValue);

  console.log("[CUSTOM TABLE DATE FILTER] Parsed - FilterStr:", filterDateStr, "RowStr:", rowDateStr);

  if (filterDateStr && rowDateStr) {
    const isMatch = filterDateStr === rowDateStr;
    console.log("[CUSTOM TABLE DATE FILTER] Match result:", isMatch);
    return isMatch;
  }

  // Default fallback to text-based matching if parsing fails or isn't a date
  if (Array.isArray(rowValue)) {
    return rowValue.some(val => String(val).toLowerCase().includes(String(filterValue).toLowerCase()));
  }
  if (typeof rowValue === 'string') {
    return rowValue.toLowerCase().includes(String(filterValue).toLowerCase());
  }
  return String(rowValue) === String(filterValue);
};

// Define types for the props
interface CustomTableProps {
  columns: any; // Replace 'any' with your specific column type
  data?: any[]; // Replace 'any' with your specific data type
  hiddenColumns?: string[];
  title?: string;
  filteredData?: (table: any) => void; // Replace 'any' with your specific table type
  handlePaginationChange?: (pagination: any) => void; // Replace 'any' with your specific pagination type
  inWhiteScreen?: boolean;
  creatingRow?: boolean;
  onClickRow?: (row: any) => void; // Replace 'any' with your specific row typ
  globalSearchValue?: string;
  rowCount?: number; // Optional for backward compatibility
  renderBottomToolbar?: React.ReactNode;
  onCreatingRowCancel?: () => void;
  onCreatingRowSave?: () => void;
  onEditingRowSave?: () => void;
  onEditingRowCancel?: () => void;
  setPaginations?: (pagination: any) => void; // Replace 'any' with your specific pagination type
  paginations?: { pageIndex: number; pageSize: number };
  enableTopToolbar?: boolean;
  enableBottomToolbar?: boolean;
  columnFilter?: any; // Replace 'any' with your specific column filter type
  darkBackground?: boolean;
  enableRowActions?: boolean;
  enableRowSelection?: boolean;
  enableColumnFilters?: boolean;
  enableEditing?: boolean;
  renderRowActions?: (row: any) => React.ReactNode; // Replace 'any' with your specific row type
  enableSorting?: boolean;
  columnVisibility?: any; // Replace 'any' with your specific column visibility type
  handleSearchBar?: boolean;
  handleCustomFilter?: boolean;
  enableExpanding?: boolean;
  searchBarFiterProps?: any; // Replace 'any' with your specific search bar filter props type
  searchBarOnChange?: (value: string) => void; // Replace 'string' with your specific search value type
  enablePagination?: boolean;
  isRefetchingTodos?: boolean;
  isLoadingTodos?: boolean;
  renderTopToolBar?: (table: any) => React.ReactNode;
  renderDetailPanel?: ({ row }: { row: any }) => React.ReactNode;
  onFullScreen?: (table: any) => void;
  enableStickyHeader?: boolean;
  maxHeight?: string | number;
  getRowBackgroundColor?: (row: any) => string | undefined;
  nowrapHeaders?: boolean;
}

const getColumnOrder = (columns: any[]): string[] =>
  columns
    .map((column) => column.id ?? column.accessorKey)
    .filter((columnId): columnId is string => Boolean(columnId));

const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  data = [],
  hiddenColumns = [],
  title,
  filteredData,
  setPaginations,
  paginations = { pageIndex: 0, pageSize: 10 },
  enableTopToolbar = true,
  enableBottomToolbar = true,
  columnFilter,
  darkBackground = false,
  enableRowActions = true,
  rowCount,
  enableRowSelection = false,
  enableColumnFilters = true,
  enableEditing = false,
  enableExpanding,
  renderRowActions = ({ row }) => <></>,
  enableSorting = true,
  onClickRow = () => { },
  handleSearchBar = true,
  searchBarFiterProps = {},
  searchBarOnChange,
  renderTopToolBar = ({ table }) => {
    return (
      <Box
        sx={{ display: "flex", justifyContent: 'flex-end', gap: "0.5rem", alignItems: "center", padding: "0.5rem", backgroundColor: '#FFFFFF' }}
      >
        <MRT_ShowHideColumnsButton table={table} />
      </Box>
    );
  },
  renderDetailPanel,
  enablePagination = true,
  isRefetchingTodos = false,
  isLoadingTodos = false,
  onFullScreen,
  columnVisibility: propColumnVisibility,
  enableStickyHeader = false,
  maxHeight,
  getRowBackgroundColor,
  nowrapHeaders = false,
}) => {
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => getColumnOrder(columns));
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const initialVisibility = hiddenColumns.reduce((acc, column) => {
      acc[column] = false;
      return acc;
    }, {} as Record<string, boolean>);

    if (propColumnVisibility) {
      return { ...initialVisibility, ...propColumnVisibility };
    }
    return initialVisibility;
  });

  const lastPropsRef = useRef({
    hiddenColumnsJSON: JSON.stringify(hiddenColumns),
    propColumnVisibilityJSON: JSON.stringify(propColumnVisibility)
  });

  useEffect(() => {
    if (columnFilter) {
      setColumnFilters(columnFilter);
    }
  }, [columnFilter]);

  useEffect(() => {
    const nextColumnOrder = getColumnOrder(columns);
    setColumnOrder((prevColumnOrder) => {
      const isSameOrder =
        prevColumnOrder.length === nextColumnOrder.length &&
        prevColumnOrder.every((columnId, index) => columnId === nextColumnOrder[index]);
      return isSameOrder ? prevColumnOrder : nextColumnOrder;
    });
  }, [columns]);

  useEffect(() => {
    const currentHiddenJSON = JSON.stringify(hiddenColumns);
    const currentVisibilityJSON = JSON.stringify(propColumnVisibility);

    const propsChanged =
      lastPropsRef.current.hiddenColumnsJSON !== currentHiddenJSON ||
      lastPropsRef.current.propColumnVisibilityJSON !== currentVisibilityJSON;

    if (propsChanged) {
      lastPropsRef.current = {
        hiddenColumnsJSON: currentHiddenJSON,
        propColumnVisibilityJSON: currentVisibilityJSON
      };

      const initialVisibility = hiddenColumns.reduce((acc, column) => {
        acc[column] = false;
        return acc;
      }, {} as Record<string, boolean>);

      setColumnVisibility({ ...initialVisibility, ...propColumnVisibility });
    }
  }, [propColumnVisibility, hiddenColumns]);

  const processedColumns = React.useMemo(() => {
    const injectDateFilterFn = (cols: any[]): any[] => {
      return cols.map((col) => {
        if (col.filterVariant === "date") {
          return {
            ...col,
            filterFn: customDateFilterFn
          };
        }
        if (col.columns) {
          return {
            ...col,
            columns: injectDateFilterFn(col.columns)
          };
        }
        return col;
      });
    };
    return injectDateFilterFn(columns);
  }, [columns]);

  const table = useMaterialReactTable({
    columns: processedColumns,
    data,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    enableColumnActions: false,
    enableSorting,
    enableColumnFilters,
    columnFilterDisplayMode: "popover",
    enableStickyHeader: enableStickyHeader,
    muiTableContainerProps: {
      sx: {
        maxHeight: maxHeight || undefined,
      },
    },
    filterFns: {
      equals: (row, id, filterValue) => {
        const val = row.getValue(id);
        if (filterValue === undefined || filterValue === null || filterValue === '') {
          return true;
        }
        if (val === undefined || val === null) {
          return false;
        }
        return String(val).toLowerCase() === String(filterValue).toLowerCase();
      },
    },
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small",
      SelectProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              borderRadius: '10px',
              boxShadow: '0px 10px 30px rgba(44, 71, 72, 0.15)',
              border: '1px solid rgba(44, 71, 72, 0.1)',
              marginTop: '4px',
              '& .MuiMenuItem-root': {
                fontSize: '12px',
                fontFamily: 'Inter, Roboto, sans-serif',
                padding: '8px 14px',
                color: '#353c52',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(44, 71, 72, 0.08)',
                  color: '#2C4748',
                },
                '&.Mui-selected': {
                  backgroundColor: '#2C4748 !important',
                  color: '#ffffff',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#1e3233 !important',
                  },
                },
              },
            },
          },
        },
      },
      sx: {
        width: '260px',
        '& .MuiInputBase-root': {
          height: '34px',
          fontSize: '12px',
          backgroundColor: '#ffffff',
          color: '#2C4748',
          borderRadius: '8px',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
          '& fieldset': {
            borderColor: '#e0e0e0',
            transition: 'border-color 0.2s ease',
          },
          '&:hover fieldset': {
            borderColor: '#2C4748',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#2C4748',
            borderWidth: '1.5px',
          },
        },
        '& .MuiInputBase-input': {
          padding: '0px 12px !important',
          height: '34px',
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiSvgIcon-root': {
          color: '#2C4748',
        },
        '& .MuiIconButton-root': {
          padding: '4px',
          color: '#999999',
          '&:hover': {
            color: '#2C4748',
            backgroundColor: 'rgba(44, 71, 72, 0.05)',
          },
        },
      },
    },
    enableRowActions: enableRowActions,
    renderRowActions: renderRowActions,
    enableTopToolbar,
    enableBottomToolbar,
    enableExpanding,
    defaultColumn: {
      size: 160,
      minSize: 120,
      maxSize: 1000,
    },
    localization: {
      //   noRecordsToDisplay: <div>No Records to Display</div>,
      rowsPerPage: '',
      hideAll: "Hide All",
      showAll: "Show All",
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
      "mrt-row-numbers": {
        enableResizing: true,
        size: 100,
        grow: true,
      },
    },
    ...(rowCount !== undefined && { rowCount }), // Only include if provided
    enableRowNumbers: false,
    layoutMode: "grid",
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    enableFacetedValues: false,
    enableFilterMatchHighlighting: false,
    state: {
      pagination: paginations,
      columnFilters,
      columnOrder,
      density: "compact",
      columnVisibility,
      //   globalFilter: "", // Assuming you want to pass this as a prop
      isLoading: isLoadingTodos,
      showProgressBars: isRefetchingTodos,
    },
    initialState: {
      columnSizing: { maxSize: 500 },
      columnVisibility,
      columnPinning: {
        left: ["Action"],
        right: ['mrt-row-actions'],
      },
    },
    onColumnOrderChange: setColumnOrder,
    onPaginationChange: setPaginations,
    muiTopToolbarProps: {
      sx: {
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    renderDetailPanel: renderDetailPanel,
    muiTableHeadCellProps: ({ column }: { column: any }) => {
      const isFiltered = column?.getIsFiltered?.();
      return {
        sx: {
          fontWeight: 'bold',
          whiteSpace: nowrapHeaders ? 'nowrap !important' : 'normal !important',
          wordBreak: 'break-word !important',
          '& *': {
            whiteSpace: nowrapHeaders ? 'nowrap !important' : 'normal !important',
            overflowWrap: 'anywhere !important',
            textOverflow: 'unset !important',
          },
          color: '#ffffff', // Direct colors
          backgroundColor: '#2C4748',
          '& .Mui-TableHeadCell-Content': {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            alignItems: 'center',
            width: '100% !important',
            gap: '8px',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            overflow: 'visible !important',
            textOverflow: 'unset !important',
            whiteSpace: nowrapHeaders ? 'nowrap !important' : 'normal !important',
            lineHeight: 1.25,
          },
          '& .Mui-TableHeadCell-Content-Labels': {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            alignItems: 'center',
            flexGrow: '1 !important',
            width: '100% !important',
            order: '1 !important',
            whiteSpace: nowrapHeaders ? 'nowrap !important' : 'normal !important',
            overflow: 'visible !important',
          },
          '& .Mui-TableHeadCell-Content-Actions': {
            display: 'flex !important',
            alignItems: 'center',
            justifyContent: 'flex-end !important',
            order: '2 !important',
            flexShrink: 0,
          },
          '& .MuiTableSortLabel-root': {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            width: '100% !important',
            color: '#fff', // text color of the label
            '&:hover': {
              color: '#fff',
            },
            '&.Mui-active': {
              color: '#fff',
            },
          },
          '& .MuiTableSortLabel-icon': {
            color: '#fff !important', // force the icon to white
          },
          // Premium bright white icons in the dark-teal header cell for maximum contrast
          '& .MuiIconButton-root': {
            color: isFiltered ? '#F0CD7F !important' : '#ffffff !important',
            opacity: isFiltered ? '1 !important' : 0.8,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
            },
          },
          '& .MuiSvgIcon-root': {
            color: isFiltered ? '#F0CD7F !important' : '#ffffff !important',
          },
        },
      };
    },
    muiTableHeadRowProps: {
      sx: {
        whiteSpace: 'nowrap !important',
        '& *': {
          whiteSpace: 'nowrap !important',
        },
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      const customBg = getRowBackgroundColor ? getRowBackgroundColor(row) : undefined;
      return {
        hover: false,
        sx: {
          fontWeight: 400,
          bgcolor: customBg || (darkBackground ? '#444' : '#fff'), // Direct colors
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: customBg || (darkBackground ? '#555' : 'rgba(0, 0, 0, 0.04)'),
            filter: customBg ? 'brightness(0.95)' : undefined,
          },
        },
        // onClick: onClickRow,
        onClick: () => {
          onClickRow(row.original);
        }
      };
    },
    muiTableBodyCellProps: {
      sx: {
        padding: "8px",
        fontWeight: 400,
        textWrap: 'wrap',
        borderBottom: '1px solid rgba(44, 71, 72, 0.2) !important',
        backgroundColor: 'inherit !important',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bgcolor: darkBackground ? '#333' : '#fff', // Direct colors
      },
    },
    positionActionsColumn: "last",
    renderTopToolbar: renderTopToolBar,
    paginationDisplayMode: "pages",
    manualPagination: true,
    muiTablePaperProps: ({ table }) => ({
      style: {
        margin: table.getState().isFullScreen ? "0 0 0 16%" : undefined,
        width: table.getState().isFullScreen ? "100%" : undefined,
        maxWidth: table.getState().isFullScreen ? "84%" : undefined,
        padding: table.getState().isFullScreen ? "2rem 2rem" : undefined,
        backgroundColor: table.getState().isFullScreen ? '#f4f4f4' : undefined, // Direct colors
      },
    }),
    muiPaginationProps: {
      color: "primary",
      className: "page",
      showRowsPerPage: false,
      showFirstButton: false,
      showLastButton: false,
      variant: "outlined",
      sx: {
        boxShadow: "none",
        '.Mui-selected': {
          color: "#353c52 !important",
          borderColor: "#353c52 !important",
          fontWeight: "bold",
          borderWidth: "2px",
          backgroundColor: 'transparent !important',
        },
      },
    },
    positionCreatingRow: "bottom",
    editDisplayMode: "row",
    createDisplayMode: "row",
    enableEditing,
    enableCellActions: true,
    enablePagination,
  });



  return (
    <TableWrapper style={{ maxWidth: "100%", overflow: "auto", width: "100%" }}>
      <MaterialReactTable table={table} />
    </TableWrapper>
  );
};

export default React.memo(CustomTable);
